import React, { Fragment, FC, useState } from 'react';
import { Box, Text, Checkbox, TextFieldSize, Select, Button, ButtonAppearance } from '@bechtel/gds';
import { t, Trans } from '@lingui/macro';
import { Field, FieldArray } from 'formik';
import { CaddySelector, CrewMemberSelector, FillerMaterialSelector } from '~/components/molecules';
import {
  CaddyFillerMaterialDiameter,
  RodFillerMaterial,
  RodFillerMaterialDetail,
  RodRequestCaddy,
} from '@bechtel/types';
import { StencilSelector } from '@bechtel/client-weld-common';
import { WelderStyled, StencilSelectorStyled } from './styled';
import { findIndex, map } from 'lodash';
import { AddCaddyDetailProps } from './types';
import { Formik, FormikProps } from 'formik';
import { object, string, array } from 'yup';
import { AddCaddyFormikState } from '../types';

const initialValues: AddCaddyFormikState = {
  caddy: undefined,
  fillerMaterial: undefined,
  stencils: [],
  pickedUpBy: undefined,
};

export const validationSchema = object().shape({
  fillerMaterial: object()
    .shape({
      fillerMaterial: object().required(t`Filler Material is mandatory`),
      diameters: array()
        .of(
          object().shape({
            heatLotNumber: object().shape({
              id: string().required(t`Heat/Lot Number ID is mandatory`),
              label: string().required(t`Heat/Lot Number Label is mandatory`),
            }),
            diameter: object().shape({
              id: string().required(t`Diameter ID is mandatory`),
              label: string().required(t`Diameter Label is mandatory`),
            }),
          })
        )
        .min(1, t`Select at least one Rod diameter`),
    })
    .required(t`Filler Material is mandatory`),
  pickedUpBy: object().required(t`Select value for Picked by`),
  stencils: array().min(1, t`Select at least 1 welder`),
  caddy: object().shape({
    id: string().required(),
    label: string().required(),
  }),
});

const RodHeatLotNumberItem = ({ item }: any) => {
  return (
    <Text size="400" color="grey.1000">
      {item?.label ? item.label : <Trans>Enter Heat/Lot Number</Trans>}
    </Text>
  );
};

export const AddCaddyDetail: FC<AddCaddyDetailProps> = ({ welders, onAddCaddy, selectedCaddies }) => {
  const [fillerMaterialData, setFillerMaterialData] = useState<RodFillerMaterial | undefined>();
  const onSelectFillerMaterial = (fillerMaterial: RodFillerMaterial) => {
    setFillerMaterialData(fillerMaterial);
  };
  return (
    <Formik
      enableReinitialize={true}
      validationSchema={validationSchema}
      initialValues={{ ...initialValues, pickedUpBy: welders?.length === 1 ? welders[0] : undefined }}
      onSubmit={(values, helpers) => {
        const { caddy, fillerMaterial, stencils, pickedUpBy } = values;
        const caddyData: RodRequestCaddy = {
          caddy,
          stencils,
          fillerMaterial,
          pickedUpBy,
        };
        onAddCaddy(caddyData);
        setFillerMaterialData(undefined);
        helpers.resetForm();
      }}
    >
      {(props: FormikProps<AddCaddyFormikState>) => {
        const { values, dirty, isValid, handleSubmit, setFieldValue } = props;

        return (
          <Fragment>
            <Box justifyContent={{ sm: 'space-between' }}>
              <Box
                flexBasis={{ sm: '50%' }}
                mr={{ sm: '450' }}
                flexDirection={{ sm: 'column' }}
                pl={{ sm: '500' }}
                pt={{ sm: '600' }}
                mb={{ sm: '500' }}
              >
                <Text weight="600" size="400" color="grey.1000">
                  <Trans>Caddy Details</Trans>
                </Text>
                <Box mt={{ sm: '500' }} data-testid="caddy-number">
                  <Field
                    component={(props) => (
                      <CaddySelector fieldName="caddy" selectedCaddies={selectedCaddies} {...props} />
                    )}
                  />
                </Box>
                <Box mt={{ sm: '550' }} data-testid="filler-material">
                  <Field
                    component={(props) => (
                      <FillerMaterialSelector
                        onSelect={(fillerMaterial) => {
                          setFieldValue('fillerMaterial.diameters', []);
                          onSelectFillerMaterial(fillerMaterial);
                        }}
                        fieldName="fillerMaterial.fillerMaterial"
                        {...props}
                      />
                    )}
                  />
                </Box>
              </Box>
              <WelderStyled>
                <Text size="400" weight="600" color="grey.1000">
                  <Trans>Welder(s)</Trans>
                </Text>
                <StencilSelectorStyled data-testid="issued-list">
                  <Field
                    component={(props) => (
                      <StencilSelector
                        {...props}
                        loading={false}
                        placeholder={t`Add Welder`}
                        formLabel={t`Issued to`}
                        fieldName="stencils"
                        items={welders}
                      />
                    )}
                  />
                </StencilSelectorStyled>
                <Box mt={{ sm: '550' }} data-testid="crew-member-selector">
                  <Field
                    component={(props) => <CrewMemberSelector items={welders} fieldName="pickedUpBy" {...props} />}
                  />
                </Box>
              </WelderStyled>
            </Box>
            <Box px={{ sm: '500' }} mt={{ sm: '500' }} pb={{ sm: '450' }} justifyContent={{ sm: 'space-between' }}>
              <Box
                pr={{ sm: '450' }}
                flexBasis={{ sm: '50%' }}
                flexDirection={{ sm: 'column' }}
                data-testid="rod-details-list"
              >
                <FieldArray name="fillerMaterial.diameters">
                  {({ push, remove }) => {
                    return (
                      <>
                        {map(fillerMaterialData?.rodFillerMaterialDetails, (item: RodFillerMaterialDetail) => {
                          const itemIndex = findIndex(
                            values?.fillerMaterial?.diameters,
                            (diameter: CaddyFillerMaterialDiameter) => {
                              return item.diameter.id === diameter.diameter.id;
                            }
                          );
                          return (
                            <Box
                              alignItems={{ sm: 'center' }}
                              key={item.id}
                              justifyContent={{ sm: 'space-between' }}
                              pb={{ sm: '400' }}
                            >
                              <Box
                                flexBasis={{ sm: '30%' }}
                                mr={{ sm: '400' }}
                                alignItems={{ sm: 'center' }}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  if (itemIndex !== -1) {
                                    remove(itemIndex);
                                  } else {
                                    push({
                                      diameter: { id: item.diameter.id, label: item.diameter.label },
                                      heatLotNumber: { id: '', label: '' },
                                    });
                                  }
                                }}
                              >
                                <Box mr={{ sm: '400' }}>
                                  <Checkbox checked={itemIndex !== -1} readOnly />
                                </Box>
                                <Text family="graphik" size="300" color="bechtel.blue.800">
                                  {item.diameter.label}&nbsp;<Trans>Dia</Trans>
                                </Text>
                              </Box>
                              <Select
                                itemComponent={RodHeatLotNumberItem}
                                selectedComponent={RodHeatLotNumberItem}
                                size={TextFieldSize.SMALL}
                                items={item.rodHeatLotNumber}
                                selectedItem={
                                  values?.fillerMaterial?.diameters?.length
                                    ? values?.fillerMaterial?.diameters[itemIndex]?.heatLotNumber
                                    : t`Enter Heat/Lot Number`
                                }
                                onSelectedItemChange={async (changes) => {
                                  setFieldValue(`fillerMaterial.diameters[${itemIndex}].heatLotNumber`, {
                                    id: changes.selectedItem?.id,
                                    label: changes.selectedItem?.label,
                                  });
                                }}
                                disabled={itemIndex === -1}
                              />
                            </Box>
                          );
                        })}
                      </>
                    );
                  }}
                </FieldArray>
              </Box>
              <Box justifyContent={{ sm: 'flex-end' }} alignItems={{ sm: 'flex-end' }}>
                <Button
                  appearance={ButtonAppearance.PRIMARY}
                  disabled={!dirty || !isValid}
                  onClick={() => handleSubmit()}
                >
                  <Box px={{ sm: '400' }}>
                    <Text color="white" size="500" weight="600">
                      <Trans>Add Caddy</Trans>
                    </Text>
                  </Box>
                </Button>
              </Box>
            </Box>
          </Fragment>
        );
      }}
    </Formik>
  );
};
